import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { ProcessRunDetailDto } from '@api/processRuns';
import { useSseStream, type SseEvent } from '@hooks/useSseStream';
import { createEventHandler } from '../editor/runSession/eventHandler';
import { computeReconnectBackoff, sleepUntilAborted } from '../editor/runSession/reconnect';
import { initialState } from '../editor/runSession/state';
import type { NodeLiveState, ProcessRunSessionState } from '../editor/runSession/types';
import { connectAndRead } from '../editor/runSession/useProcessRunSession';
import { processRunDetailQueryKey } from '../hooks/processRunsQueryKeys';

/** Envelopes after which the persisted run row is worth reading again. */
const REFRESH_ON: ReadonlySet<string> = new Set([
  'run_status',
  'node_state',
  'artifact',
  'gate_decided',
  'stream_end',
]);

const isRunDetail = (data: Record<string, unknown>): data is Record<string, unknown> & {
  run: Record<string, unknown>;
} => typeof data.run === 'object' && data.run !== null;

/**
 * Follows one run's stream for as long as it is in flight. A node's transcript
 * only reaches the row once the node finishes, so while it works the stream is
 * the sole source of its agent log; every state envelope also refreshes the run
 * detail query the page renders from.
 */
export function useRunDetailLive(
  processId: string,
  runId: string,
  enabled: boolean,
): ReadonlyMap<string, NodeLiveState> {
  const queryClient = useQueryClient();
  const [state, setState] = useState<ProcessRunSessionState>(initialState);
  // Same node ids recur across runs of one process, so a run's transcripts
  // must not survive into the next run shown here.
  const [prevRunId, setPrevRunId] = useState(runId);
  if (prevRunId !== runId) {
    setPrevRunId(runId);
    setState(initialState());
  }
  const mountedRef = useRef(false);
  const streamAbortRef = useRef<AbortController | null>(null);
  const loopAbortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const safeSetState = useCallback(
    (updater: (prev: ProcessRunSessionState) => ProcessRunSessionState) => {
      if (mountedRef.current) setState(updater);
    },
    [],
  );
  const abortStream = useCallback(() => {
    streamAbortRef.current?.abort();
    streamAbortRef.current = null;
  }, []);
  const abortLoop = useCallback(() => {
    loopAbortRef.current?.abort();
    loopAbortRef.current = null;
  }, []);

  const handlerRef = useRef<(event: SseEvent) => void>(() => undefined);
  useEffect(() => {
    const handle = createEventHandler({
      processId,
      queryClient,
      safeSetState,
      abortStream,
      abortReconnect: abortLoop,
      unchosenBranchNodeIds: () => [],
    });
    const detailKey = processRunDetailQueryKey(processId, runId);
    handlerRef.current = (event) => {
      handle(event);
      if (event.type === 'reconnected' && isRunDetail(event.data)) {
        queryClient.setQueryData(detailKey, event.data as unknown as ProcessRunDetailDto);
      } else if (REFRESH_ON.has(event.type)) {
        queryClient.invalidateQueries({ queryKey: detailKey }).catch(() => undefined);
      }
    };
  }, [processId, runId, queryClient, safeSetState, abortStream, abortLoop]);

  const onEvent = useCallback((event: SseEvent) => handlerRef.current(event), []);
  const { readStream } = useSseStream(onEvent);

  useEffect(() => {
    if (!enabled) return undefined;
    const loop = new AbortController();
    loopAbortRef.current = loop;
    const follow = async () => {
      let attempt = 0;
      while (!loop.signal.aborted) {
        const stream = new AbortController();
        streamAbortRef.current = stream;
        const opened = await connectAndRead(processId, runId, stream, loop, readStream);
        if (loop.signal.aborted) return;
        const delay = opened ? 0 : computeReconnectBackoff(attempt);
        attempt = opened ? 0 : attempt + 1;
        await sleepUntilAborted(delay, loop.signal);
      }
    };
    follow().catch(() => undefined);
    return () => {
      loop.abort();
      streamAbortRef.current?.abort();
      streamAbortRef.current = null;
      loopAbortRef.current = null;
    };
  }, [enabled, processId, runId, readStream]);

  return state.perNode;
}
